/**
 * OS notifications for Dev Board activity — the same contract as the livechat
 * ones (utils/livechatOsNotifications.ts): only while the dashboard tab is out
 * of view, since the in-app toast already covers a visible tab, and clicking
 * focuses the dashboard and opens the ticket.
 *
 * Permission is shared per-origin, so a user who granted it for livechat is
 * already covered here and vice versa; the permission helpers are reused from
 * the livechat module rather than duplicated.
 */
import { getLivechatOsNotificationPermission as getOsNotificationPermission } from "./livechatOsNotifications";

type BrowserWindow = Window & typeof globalThis;

const OS_NOTIFICATION_TAG = "di-dev-board-notification";

interface DevBoardOsNotificationOptions {
  title: string;
  body?: string;
  onOpen?: () => void;
  /** Distinguishes notifications so a new mention does not silently replace an
   * unclicked earlier one. */
  eventKey?: string | null;
  browserWindow?: BrowserWindow;
}

function isBrowserTabInView(browserWindow: BrowserWindow): boolean {
  const browserDocument = browserWindow?.document;
  if (!browserDocument) return true;
  if (browserDocument.visibilityState === "hidden") return false;
  return typeof browserDocument.hasFocus === "function" ? browserDocument.hasFocus() : true;
}

/** Shows an OS notification only while the dashboard tab is out of view. */
export function showDevBoardOsNotification({
  title,
  body,
  onOpen,
  eventKey,
  browserWindow = window,
}: DevBoardOsNotificationOptions): Notification | null {
  if (
    getOsNotificationPermission(browserWindow) !== "granted" ||
    isBrowserTabInView(browserWindow)
  ) {
    return null;
  }

  try {
    const notification = new browserWindow.Notification(title, {
      body,
      tag: eventKey ? `${OS_NOTIFICATION_TAG}-${eventKey}` : OS_NOTIFICATION_TAG,
      renotify: true,
    } as NotificationOptions);
    notification.onclick = () => {
      try {
        browserWindow.focus();
      } catch {
        // Browser focus may be blocked.
      }
      onOpen?.();
      notification.close();
    };
    return notification;
  } catch {
    return null;
  }
}
